import { getDateFnsLocale } from '@eventbrite/datetime-fns';
import { gettext } from '@eventbrite/i18n';
import {
    Button,
    CalendarIcon,
    CloseIcon,
    TextField,
    TextFieldProps,
    Typography,
} from '@eventbrite/marmalade';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { format, isValid, Locale, parse, startOfDay } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useMedia, useUpdateEffect } from 'react-use';
import { useDefaultLocale } from '../../hooks/useDefaultLocale';
import { Datepicker } from './Datepicker';
import styles from './DateRangeInput.module.scss';

export interface DateRangeInputProps
    extends Omit<TextFieldProps, 'type' | 'value' | 'onChange'> {
    value?: Date[];
    onChange: (d: Date[]) => void;
    getActionLabel?: (count: number) => string;
    headerLabel?: string;
    locale?: string;
}

export enum DateRangeInputTestIds {
    Input = 'DateInputInput',
    Calendar = 'DateInputCalendar',
    SelectDates = 'DateInputSelectDates',
}

export const DateRangeInput = React.forwardRef<
    HTMLInputElement,
    DateRangeInputProps
>(
    (
        {
            onChange,
            onBlur,
            value = defaultDatesValue,
            getActionLabel,
            headerLabel,
            locale,
            ...fieldProps
        }: DateRangeInputProps,
        ref,
    ) => {
        const isTouchDevice = useMedia('(hover: none) and (pointer: coarse)');
        const isMobile = useMedia('(max-width: 792px)');
        const browserLocale = useDefaultLocale(locale);
        const dateFnsLocale = getDateFnsLocale(browserLocale);

        const [validDates, setValidDates] = useState<Date[]>(value);
        const [open, setOpen] = useState(false);
        const [editing, setEditing] = useState(false);
        const [fieldValue, setFieldValue] = useState(
            formatValue({ dates: validDates, locale: browserLocale }),
        );

        const selectDateLabel = useMemo(
            () => getActionLabel?.(validDates.length),
            [validDates, getActionLabel],
        );

        useUpdateEffect(() => {
            setValidDates(value);
            formatValue({ dates: value, locale: browserLocale });
        }, [value, browserLocale]);

        useUpdateEffect(() => {
            if (!validDates || validDates.length === 0) {
                setFieldValue('');
            } else {
                setFieldValue(
                    formatValue({ dates: validDates, locale: browserLocale }),
                );
            }
        }, [validDates, browserLocale]);

        const onOpenChangeHandler = useCallback((open: boolean) => {
            setOpen(open);
        }, []);

        const onChangeFieldHandler = useCallback((value: string) => {
            setFieldValue(value);
        }, []);

        const dispatchDates = useCallback(
            (dates: Date[]) => {
                if (areDifferentDates(dates, value)) {
                    onChange(dates);
                    setValidDates(dates);
                }
            },
            [onChange, value],
        );

        const recoverLastValidDates = useCallback(() => {
            setFieldValue(
                formatValue({
                    dates: validDates,
                    locale: browserLocale,
                }),
            );

            setEditing(false);
            setOpen(false);
        }, [validDates, browserLocale]);

        const hotkeyshandler = {
            Escape: recoverLastValidDates,
        };

        const onKeyDownHandler = (
            event: React.KeyboardEvent<HTMLInputElement>,
        ): void => {
            const { key } = event;

            if (key in hotkeyshandler) {
                event.preventDefault();
                const handler =
                    hotkeyshandler[key as keyof typeof hotkeyshandler];
                handler();
            } else {
                setEditing(true);
            }
        };

        const onKeyPressHandler = useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (!/[0-9]|\/|-|,/.test(event.key)) {
                    event.preventDefault();
                }
            },
            [],
        );

        const onKeyUpHandler = useCallback(() => {
            setFieldValue(normalizeInput({ value: fieldValue }));
        }, [fieldValue]);

        const onDatesChangeHandler = useCallback((dates: Date[]) => {
            setValidDates(dates);
        }, []);

        const onSyncDatesHandler = useCallback(() => {
            const dates = parseToDate({
                value: fieldValue,
                locale: dateFnsLocale,
            });

            if (dates.every(isValid)) {
                dispatchDates(dates);
            } else if (validDates.every(isValid)) {
                recoverLastValidDates();
            }
        }, [
            validDates,
            dispatchDates,
            fieldValue,
            recoverLastValidDates,
            dateFnsLocale,
        ]);

        const onBlurHandler = useCallback(() => {
            if (editing) {
                setEditing(false);
                onSyncDatesHandler();
                onBlur?.({} as React.FocusEvent<HTMLInputElement>);
            }
        }, [onBlur, onSyncDatesHandler, editing]);

        const onSelectDateHandler = useCallback(() => {
            onSyncDatesHandler();
            setOpen(false);
        }, [onSyncDatesHandler]);

        const onClickOutsideHandler = useCallback(
            (e: Event) => {
                e.preventDefault();
                if (!editing) {
                    onSelectDateHandler();
                }
            },
            [onSelectDateHandler, editing],
        );

        const recoverLastDatesValue = useCallback(() => {
            setValidDates(value);
            setFieldValue(
                formatValue({
                    dates: value,
                    locale: browserLocale,
                }),
            );
            setOpen(false);
        }, [value, browserLocale]);

        const Component = isMobile ? Dialog : Popover;

        return (
            <Component.Root open={open} onOpenChange={onOpenChangeHandler}>
                <Component.Trigger asChild>
                    <TextField
                        {...fieldProps}
                        data-testid={DateRangeInputTestIds.Input}
                        autoComplete="off"
                        value={fieldValue}
                        onChange={onChangeFieldHandler}
                        onBlur={onBlurHandler}
                        onKeyUp={onKeyUpHandler}
                        onKeyDown={onKeyDownHandler}
                        onKeyPress={onKeyPressHandler}
                        type="text"
                        prefix={<CalendarIcon size="small" />}
                        readOnly={isTouchDevice}
                        ref={ref}
                    />
                </Component.Trigger>
                <Component.Portal>
                    <Component.Content
                        onOpenAutoFocus={isTouchDevice ? undefined : focusTrap}
                        onPointerDownOutside={onClickOutsideHandler}
                        onEscapeKeyDown={recoverLastDatesValue}
                        className={styles.content}
                        data-testid={DateRangeInputTestIds.Calendar}
                    >
                        <div className={styles.header}>
                            {headerLabel && (
                                <Typography variant="body-md-bold">
                                    {headerLabel}
                                </Typography>
                            )}

                            <button
                                className={styles.close}
                                onClick={recoverLastDatesValue}
                                aria-label={gettext('Close').toString()}
                            >
                                <CloseIcon size="medium" />
                            </button>
                        </div>

                        <Datepicker
                            mode="range"
                            onDatesChange={onDatesChangeHandler}
                            selectedDates={assureValidDates(validDates || [])}
                            locale={browserLocale}
                        />

                        {getActionLabel && (
                            <div className={styles.actions}>
                                <Button
                                    variant="primary"
                                    size="block"
                                    onClick={onSelectDateHandler}
                                    data-testid={
                                        DateRangeInputTestIds.SelectDates
                                    }
                                >
                                    {selectDateLabel}
                                </Button>
                            </div>
                        )}
                    </Component.Content>
                </Component.Portal>
            </Component.Root>
        );
    },
);

const INPUT_SEPARATOR = '-';
const RANGE_SEPARATOR = ' — ';
const DEFAULT_INPUT_DISPLAY_FORMAT = 'P';
const focusTrap = (e: Event) => e.preventDefault();
const uniqueDates = (dates: Date[]) =>
    dates.filter(
        (date, i, self) =>
            self.findIndex(
                (d) => startOfDay(d).getTime() === startOfDay(date).getTime(),
            ) === i,
    );
const formatValue = ({ dates, locale }: { dates: Date[]; locale: string }) => {
    const dateFnsLocale = getDateFnsLocale(locale);
    const stringValue = uniqueDates(dates)
        .map((d) => format(d, 'P', { locale: dateFnsLocale }))
        .join(RANGE_SEPARATOR);

    return stringValue;
};

const normalizeDates = ({ dates }: { dates: Date[] }) => {
    if (dates.length === 1) {
        return [dates[0], dates[0]];
    }

    return dates;
};
const normalizeInput = ({ value }: { value: string }) =>
    value.replaceAll(INPUT_SEPARATOR, RANGE_SEPARATOR);

const parseToDate = ({ value, locale }: { value: string; locale: Locale }) => {
    const dates = value
        .split(RANGE_SEPARATOR)
        .filter(Boolean)
        .map((d) =>
            parse(d, DEFAULT_INPUT_DISPLAY_FORMAT, new Date(), {
                locale,
            }),
        );

    return normalizeDates({ dates });
};

const assureValidDates = (dates: Date[]) => {
    if (dates.every(isValid)) {
        return dates;
    }

    return [];
};

const areDifferentDates = (dates: Date[], values: Date[]) => {
    if (dates.length !== values.length) {
        return true;
    }

    return dates.some(
        (d, i) => startOfDay(d).getTime() !== startOfDay(values[i]).getTime(),
    );
};

// See https://github.com/facebook/react/issues/18123
const defaultDatesValue: Date[] = [];
